import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAvailableOptions } from '@/hooks/useApi';

interface Corpus {
  corpus_id: string;
  name: string;
  type: 'company' | 'shared';
  file_count?: number;
  description: string;
}

interface Model {
  id: string;
  name: string;
  description: string;
  model_id: string;
}

interface Tool {
  id: string;
  name: string;
  description: string;
}

export interface AgentConfig {
  corpus_ids: string[];
  enable_google_search: boolean;
  model: string;
}

interface AvailableOptions {
  corpora: Corpus[];
  models: Model[];
  tools: Tool[];
}

interface AgentConfigContextType {
  options: AvailableOptions | undefined;
  config: AgentConfig;
  loading: boolean;
  error: string | null;
  toggleCorpus: (corpusId: string) => void;
  toggleGoogleSearch: () => void;
  setModel: (model: string) => void;
  setConfig: (config: AgentConfig) => void;
}

const AgentConfigContext = createContext<AgentConfigContextType | undefined>(undefined);

const STORAGE_KEY = 'agent_config';

// Load config from localStorage
function loadConfigFromStorage(): AgentConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading config from storage:', error);
  }
  return null;
}

// Save config to localStorage
function saveConfigToStorage(config: AgentConfig) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Error saving config to storage:', error);
  }
}

export function AgentConfigProvider({ children }: { children: ReactNode }) {
  // Use React Query hook - this will cache the API response
  const { data: options, isLoading: loading, error: queryError } = useAvailableOptions();
  
  const [agentConfig, setAgentConfig] = useState<AgentConfig>(() => {
    // Try to load from localStorage first
    const stored = loadConfigFromStorage();
    if (stored) {
      return stored;
    }
    // Default config
    return {
      corpus_ids: [],
      enable_google_search: false,
      model: 'normal'
    };
  });

  // Initialize default config when options are loaded (only if no stored config)
  useEffect(() => {
    if (options && !loadConfigFromStorage()) {
      const companyCorpus = options.corpora.find((c: Corpus) => c.type === 'company');
      if (companyCorpus) {
        const defaultConfig = {
          corpus_ids: [companyCorpus.corpus_id],
          enable_google_search: false,
          model: 'normal'
        };
        setAgentConfig(defaultConfig);
        saveConfigToStorage(defaultConfig);
      }
    }
  }, [options]);

  // Save to localStorage whenever config changes
  useEffect(() => {
    saveConfigToStorage(agentConfig);
  }, [agentConfig]);

  const toggleCorpus = (corpusId: string) => {
    setAgentConfig(prev => {
      const newConfig = {
        ...prev,
        corpus_ids: prev.corpus_ids.includes(corpusId)
          ? prev.corpus_ids.filter(id => id !== corpusId)
          : [...prev.corpus_ids, corpusId]
      };
      return newConfig;
    });
  };

  const toggleGoogleSearch = () => {
    setAgentConfig(prev => ({
      ...prev,
      enable_google_search: !prev.enable_google_search
    }));
  };

  const setModel = (model: string) => {
    setAgentConfig(prev => ({ ...prev, model }));
  };

  const value: AgentConfigContextType = {
    options,
    config: agentConfig,
    loading,
    error: queryError ? String(queryError) : null,
    toggleCorpus,
    toggleGoogleSearch,
    setModel,
    setConfig: setAgentConfig
  };

  return (
    <AgentConfigContext.Provider value={value}>
      {children}
    </AgentConfigContext.Provider>
  );
}

export function useAgentConfig() {
  const context = useContext(AgentConfigContext);
  if (context === undefined) {
    throw new Error('useAgentConfig must be used within an AgentConfigProvider');
  }
  return context;
}
